/**
 * Host bootstrap — exposes a `HostBootstrap` class that wires the
 * `HostShell` + `HostController` against the real renderers
 * (`GPTInterstitialAds` for display, `IMAInterstitialVideo` for video,
 * `ImaRewardedHostAdapter` for rewarded) and the shared `ApiClient`.
 *
 * Consumed by `sdk/src/index.ts` (the single role-aware bundle) — the
 * `GetJarAds.initHost()` public method holds a lazily-created
 * `HostBootstrap` and forwards to its `start()` / `registerGame()`.
 *
 * No module-level side effects: the class declarations + small adapter
 * factory functions add only a few hundred bytes to the bundle for
 * game-only consumers, and the expensive operations (IMA SDK load, GPT
 * script injection, `/ads/init`) all live inside `start()`.
 *
 * Trust model: `registerGame()` is the canonical attribution surface —
 * the host trusts THIS, not the iframe's handshake claim. The handshake
 * payload is a fallback (used for dev / local where the host page didn't
 * pre-register).
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ApiClient } from '../api';
import { EventEmitter } from '../core/event-emitter';
import { GPTLoader } from '../ads/gpt-loader';
import { GPTInterstitialAds } from '../ads/gpt-interstitial';
import { IMAInterstitialVideo } from '../ads/ima-interstitial-video';
import { HostShell } from './host-shell';
import { ImaRewardedHostAdapter } from './ima-rewarded-adapter';
import { createHostBidProvider, createHostRewardValidator, } from './host-api-adapters';
// Named exports — the surface Next.js / other TypeScript consumers import.
export { HostShell } from './host-shell';
export { HostController } from './host-controller';
export { ImaRewardedHostAdapter } from './ima-rewarded-adapter';
/**
 * Bootstrap class — wraps construction state so `start()` is idempotent
 * and `registerGame()` can be called before or after start() resolves.
 *
 * `sdk/src/index.ts` holds a lazily-created instance of this class. It is
 * NOT auto-instantiated at module load — a top-frame page that only ever
 * embeds games (no `initHost()`) must not accidentally become a host.
 */
export class HostBootstrap {
    constructor() {
        this.shell = null;
        this.apiClient = null;
        this.eventEmitter = null;
        this.sessionId = null;
        this.startPromise = null;
        this.pendingGameRegistrations = [];
    }
    /**
     * Stand up the host bridge. Idempotent — repeated calls return the same
     * in-flight promise (or resolve immediately if already started). Failure
     * to reach the backend does NOT prevent the controller from attaching:
     * the bridge still ACKs handshakes; ads just no-fill until init succeeds.
     */
    start(config) {
        if (this.startPromise)
            return this.startPromise;
        this.startPromise = this.startInternal(config).catch((err) => {
            // Don't poison the promise — caller may retry. We log and reset.
            console.error('[GetJarAdsHost] start() failed:', err);
            this.startPromise = null;
            throw err;
        });
        return this.startPromise;
    }
    startInternal(config) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            this.apiClient = new ApiClient(config.apiUrl);
            this.eventEmitter = new EventEmitter(this.apiClient);
            this.eventEmitter.setAdContextProvider(() => 'ON_SITE');
            // /ads/init for the host's own session. SAME payload shape the game's
            // init() sends — the backend treats us as just another caller and the
            // adUnits / videoConfig / pacing it returns are what the host
            // renderers read. Without this the GPT interstitial renderer would be
            // constructed with an empty ad-unit path and call
            // `defineOutOfPageSlot('', INTERSTITIAL)`, which Google rejects.
            let init = null;
            try {
                init = yield this.apiClient.init(config.appId, config.publisher, {
                    privacy: config.privacy,
                });
                this.sessionId = init.sessionId;
                this.eventEmitter.setSessionId(init.sessionId);
            }
            catch (err) {
                // Fail-open: keep the bridge alive even when /ads/init is unreachable
                // or the credentials are wrong. The handshake still ACKs; ads
                // resolve no_ad until init recovers. Operators see the 400 in this
                // log line and know to fix the (appId, publisher) pair.
                console.warn('[GetJarAdsHost] /ads/init failed — continuing without a backend session. ' +
                    'Handshake will still ACK; ads will no_ad until init recovers.', err);
            }
            // Load GPT in parallel with bootstrap (banner renderer not used host-side
            // today, but display interstitials need it). Failure is logged; the
            // renderer will surface its own error if a show is attempted.
            try {
                yield GPTLoader.load();
            }
            catch (err) {
                console.warn('[GetJarAdsHost] GPT load failed:', err);
            }
            const interstitialRenderer = new GPTInterstitialAds(this.eventEmitter, (_b = (_a = init === null || init === void 0 ? void 0 : init.adUnits) === null || _a === void 0 ? void 0 : _a.interstitial) !== null && _b !== void 0 ? _b : '');
            const videoInterstitialRenderer = new IMAInterstitialVideo(this.eventEmitter);
            const rewardedVideoRenderer = new ImaRewardedHostAdapter(this.eventEmitter);
            // Kick off IMA preload in the background. query_ready will report the
            // adapter's `isPreloaded()` flag, so game UI lights up as soon as this
            // resolves. Errors are swallowed — the next query_ready will trigger
            // another attempt.
            void rewardedVideoRenderer.preload().catch(() => undefined);
            const getSessionId = () => this.sessionId;
            this.shell = new HostShell({
                allowedOrigins: (_c = config.allowedGameOrigins) !== null && _c !== void 0 ? _c : [],
                pacingConfig: (_d = init === null || init === void 0 ? void 0 : init.pacing) !== null && _d !== void 0 ? _d : null,
                videoInterstitialTag: (_e = init === null || init === void 0 ? void 0 : init.videoConfig) === null || _e === void 0 ? void 0 : _e.interstitialTag,
                rewardedVideoTag: (_f = init === null || init === void 0 ? void 0 : init.videoConfig) === null || _f === void 0 ? void 0 : _f.rewardedTag,
                // Server-pushed AY debug flag (mirrors backend AY_PBS_DEBUG). Host
                // shell uses it to gate verbose console output on the bid → render
                // hand-off (targeting maps + merged VAST URLs).
                ayDebug: (init === null || init === void 0 ? void 0 : init.ayDebug) === true,
                interstitialRenderer,
                videoInterstitialRenderer,
                rewardedVideoRenderer,
                bidProvider: createHostBidProvider(this.apiClient, getSessionId),
                rewardValidator: createHostRewardValidator(this.apiClient, getSessionId),
                // Forward host telemetry through the same event emitter the renderers
                // use. `ad_autoplay_blocked` lands as a normal AdsEvent row with the
                // event-type passed straight through.
                telemetry: {
                    emit: (event, data) => {
                        if (!this.eventEmitter)
                            return;
                        // Cast: telemetry event names are runtime strings, not enum members.
                        void this.eventEmitter.emit(event, data);
                    },
                },
                adUnits: {
                    interstitial: (_g = init === null || init === void 0 ? void 0 : init.adUnits) === null || _g === void 0 ? void 0 : _g.interstitial,
                    videoInterstitial: (_h = init === null || init === void 0 ? void 0 : init.adUnits) === null || _h === void 0 ? void 0 : _h.interstitial, // backend doesn't split video-interstitial unit today
                    rewardedVideo: (_j = init === null || init === void 0 ? void 0 : init.adUnits) === null || _j === void 0 ? void 0 : _j.rewarded,
                },
                viewport: config.viewport,
                ownWindow: config.ownWindow,
            });
            this.shell.start();
            // Drain any registerGame() calls queued before start() resolved.
            for (const r of this.pendingGameRegistrations) {
                this.shell.registerGame(r.origin, { appId: r.appId, publisher: r.publisher });
            }
            this.pendingGameRegistrations.length = 0;
            console.log('[GetJarAdsHost] started', {
                allowedGameOrigins: (_k = config.allowedGameOrigins) !== null && _k !== void 0 ? _k : [],
                hasSession: this.sessionId !== null,
                adProvider: (_l = init === null || init === void 0 ? void 0 : init.adProvider) !== null && _l !== void 0 ? _l : '(unknown)',
                ayDebug: (init === null || init === void 0 ? void 0 : init.ayDebug) === true,
            });
            if (init === null || init === void 0 ? void 0 : init.ayDebug) {
                console.log('[GetJarAdsHost AY DEBUG] verbose AY logging is ON (server-pushed from /ads/init). ' +
                    'Bid request/response, hb_* targeting, and merged VAST URLs will be logged.');
            }
        });
    }
    /**
     * Register a game iframe's trusted attribution. Safe to call before
     * `start()` — registrations are queued and applied once the shell is up.
     *
     * The host trusts THIS registry over the iframe's handshake payload — the
     * iframe could lie about its appId/publisher, but the host is the one
     * embedding the iframe and knows the truth.
     */
    registerGame(input) {
        if (this.shell) {
            this.shell.registerGame(input.origin, { appId: input.appId, publisher: input.publisher });
        }
        else {
            this.pendingGameRegistrations.push(input);
        }
    }
    /** Tear down the listener. Idempotent. */
    stop() {
        var _a;
        (_a = this.shell) === null || _a === void 0 ? void 0 : _a.stop();
        this.shell = null;
        this.apiClient = null;
        this.eventEmitter = null;
        this.sessionId = null;
        this.startPromise = null;
    }
    /** Escape hatch for advanced consumers / tests. */
    getShell() {
        return this.shell;
    }
}
/**
 * Internal: factory for a fresh bootstrap instance. Tests use this to
 * avoid singleton-cross-test pollution.
 * @internal
 */
export function __createHostBootstrapForTests() {
    return new HostBootstrap();
}
